import React, { useState } from 'react';
import { X } from 'lucide-react';
import { Button } from '../ui/Button';
import { Checkbox } from '../ui/Checkbox';

interface NewEventDialogProps {
  onClose: () => void;
  // eslint-disable-next-line no-unused-vars
  onSubmit: (data: { title: string; content: string; date: string; startTime: string; endTime: string; explanation: boolean }) => void;
}

export function NewEventDialog({ onClose, onSubmit }: NewEventDialogProps) {
  const [form, setForm] = useState({
    title: '',
    content: '',
    date: '',
    startTime: '',
    endTime: '',
    explanation: false
  });

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    if (form.title.trim() && form.content.trim() && form.date && form.startTime && form.endTime) {
      onSubmit({
        title: form.title,
        content: form.content,
        date: form.date,
        startTime: form.startTime,
        endTime: form.endTime,
        explanation: form.explanation
      });
    }
  };

  return (
    <div className="fixed inset-0 bg-black/20 backdrop-blur-sm flex items-center justify-center p-4 z-50" onClick={onClose}>
      <div className="bg-white rounded-xl shadow-xl max-w-2xl sm:w-full" onClick={e => e.stopPropagation()}>
        <div className="flex items-center justify-between p-4 border-b">
          <h2 className="text-lg font-semibold">Legg til arrangement</h2>
          <button
            onClick={onClose}
            className="p-1 hover:bg-gray-100 rounded-lg transition-colors"
          >
            <X className="h-5 w-5" />
          </button>
        </div>
        
        <form onSubmit={handleSubmit} className="sm:p-6 p-4 space-y-4">
          <div className="space-y-2">
            <label className="block text-sm font-medium text-gray-700">
              Tittel
            </label>
            <input
              type="text"
              value={form.title}
              onChange={(e) => setForm(prev => ({ ...prev, title: e.target.value }))}
              placeholder="Skriv en beskrivende tittel"
              className="w-full px-3 py-2 border rounded-lg focus:ring-1 focus:ring-condo-dark focus:border-condo-med focus:outline-none min-h-[20px] border-gray-200 sm:text-base text-sm"
              required
            />
          </div>

          <div className="space-y-2">
            <label className="block text-sm font-medium text-gray-700">
              Innhold
            </label>
            <textarea
              value={form.content}
              onChange={(e) => setForm(prev => ({ ...prev, content: e.target.value }))}
              className="w-full px-3 py-2 border rounded-lg focus:ring-1 focus:ring-condo-med focus:border-condo-dark focus:outline-none min-h-[200px] border-gray-200 sm:text-base text-sm"
              placeholder="Skriv arrangementets innhold..."
              required
            />
          </div>

          <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
            <div className="space-y-2">
              <label className="block text-sm font-medium text-gray-700">
                Dato
              </label>
              <input
                type="date"
                value={form.date}
                onChange={(e) => setForm(prev => ({ ...prev, date: e.target.value }))}
                min={new Date().toISOString().split('T')[0]}
                className="w-full px-3 py-2 border rounded-lg focus:ring-1 focus:ring-condo-dark focus:border-condo-med focus:outline-none border-gray-200 sm:text-base text-sm"
                required
              />
            </div>

            <div className="space-y-2">
              <label className="block text-sm font-medium text-gray-700">
                Starttid
              </label>
              <input
                type="time"
                value={form.startTime}
                onChange={(e) => setForm(prev => ({ ...prev, startTime: e.target.value }))}
                className="w-full px-3 py-2 border rounded-lg focus:ring-1 focus:ring-condo-dark focus:border-condo-med focus:outline-none border-gray-200 sm:text-base text-sm"
                required
              />
            </div>

            <div className="space-y-2">
              <label className="block text-sm font-medium text-gray-700">
                Sluttid
              </label>
              <input
                type="time"
                value={form.endTime}
                onChange={(e) => setForm(prev => ({ ...prev, endTime: e.target.value }))}
                className="w-full px-3 py-2 border rounded-lg focus:ring-1 focus:ring-condo-dark focus:border-condo-med focus:outline-none border-gray-200 sm:text-base text-sm"
                required
              />
            </div>
          </div>

          <div className="flex items-center space-x-2">
            <Checkbox 
              id="explanation" 
              checked={form.explanation} 
              onCheckedChange={(checked) => setForm(prev => ({ ...prev, explanation: checked as boolean }))} 
            />
            <label htmlFor="explanation" className="text-sm text-gray-700">
              Krev begrunnelse for avslag
            </label>
          </div>
          <p className="text-xs text-gray-500">
            Når denne er huket av, må beboere gi en begrunnelse hvis de avslår arrangementet.
          </p>

          <div className="flex justify-between gap-3 sm:pt-4 sm:border-t border-gray-200">
            <Button variant="outline" onClick={onClose}>Avbryt</Button>
            <Button type="submit">Publiser</Button>
          </div>
        </form>
      </div>
    </div>
  );
}

